import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { tsToRelativeTime } from '@actual-app/core/shared/util';

import { useSyncAndDownloadMutation } from '#accounts';
import { sync } from '#app/appSlice';
import { useAccounts } from '#hooks/useAccounts';
import { useLocale } from '#hooks/useLocale';
import { useDispatch } from '#redux';

import { useUnreadAccounts } from './recalcul';
import { Section } from './ui';

// Set by Vite at build time; absent in the tests.
const build: unknown = import.meta.env.VITE_MTXBUDGET_BUILD;
const builtAt = typeof build === 'string' ? new Date(build) : null;
const dateAndTime = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

// Kept for the whole session: the last time this device refreshed its data.
let refreshedAt: number | null = null;

function Fact({ title, detail }: { title: string; detail: string }) {
  return (
    <View
      style={{
        padding: '10px 16px',
        borderBottom: `1px solid ${theme.tableBorder}`,
      }}
    >
      <Text style={{ fontWeight: 500 }}>{title}</Text>
      <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
        {detail}
      </Text>
    </View>
  );
}

/**
 * Which version this device runs and how fresh its data is, with a button to
 * fetch the latest version and one to sync with the server and the banks.
 */
export function ApplicationDonnees() {
  const { t } = useTranslation();
  const locale = useLocale();
  const dispatch = useDispatch();
  const { data: accounts = [] } = useAccounts();
  const syncAndDownload = useSyncAndDownloadMutation();
  const [busy, setBusy] = useState<'update' | 'refresh' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const updateReady = global.Actual.isUpdateReadyForDownload();
  const banksReadAt = accounts
    .map(account => account.last_sync)
    .filter((stamp): stamp is string => Boolean(stamp))
    .sort()
    .at(-1);

  const unread = useUnreadAccounts();

  async function update() {
    if (!navigator.onLine) {
      setFailure(t('Pas de connexion : la mise à jour demande le réseau.'));
      return;
    }
    setFailure(null);
    setBusy('update');
    // Either way the page reloads on the latest version.
    if (updateReady) {
      await global.Actual.applyAppUpdate();
    } else {
      await (global.Actual.reload?.() ?? window.location.reload());
    }
  }

  async function refresh() {
    setFailure(null);
    setBusy('refresh');
    try {
      const synced = await dispatch(sync()).unwrap();
      if (synced && 'error' in synced && synced.error) {
        throw new Error('sync');
      }
      const read = await syncAndDownload.mutateAsync({});
      if (typeof read === 'object' && read.error) {
        throw new Error('banks');
      }
      refreshedAt = Date.now();
    } catch {
      setFailure(
        t(
          "L'actualisation a échoué : le serveur ne répond pas. Vos données déjà chargées restent affichées.",
        ),
      );
    }
    setBusy(null);
  }

  const aside = busy
    ? '…'
    : failure || unread.length > 0
      ? t('À voir')
      : updateReady
        ? t('Mise à jour prête')
        : t('À jour');
  const asideColor = busy
    ? theme.pageTextSubdued
    : failure || updateReady || unread.length > 0
      ? theme.warningText
      : theme.noticeTextLight;

  const version = builtAt
    ? t('du {{date}}', { date: dateAndTime.format(builtAt) })
    : t('version de développement');
  const data = [
    busy === 'refresh'
      ? t('lecture des banques en cours : cela peut prendre une minute')
      : refreshedAt
        ? t('synchronisées avec le serveur {{when}}', {
            when: tsToRelativeTime(String(refreshedAt), locale),
          })
        : t("synchronisées avec le serveur à l'ouverture de l'application"),
    busy !== 'refresh' && banksReadAt
      ? t('banques lues {{when}}', {
          when: tsToRelativeTime(banksReadAt, locale),
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Section
      title={t('Application et données')}
      aside={
        <Text style={{ fontWeight: 600, color: asideColor }}>{aside}</Text>
      }
    >
      <Fact
        title={t('Version installée')}
        detail={
          busy
            ? version
            : updateReady
              ? t('{{version}} · une version plus récente existe', { version })
              : t('{{version}} · la plus récente', { version })
        }
      />
      <Fact title={t('Données')} detail={data} />
      <View style={{ gap: 8, padding: 16 }}>
        {unread.length > 0 && !busy && (
          <Text style={{ fontSize: 13, color: theme.warningText }}>
            {t(
              'Comptes non lus : {{accounts}}. Les autres sont à jour. Reliez-les de nouveau dans « Synchro bancaire ».',
              {
                accounts: unread
                  .map(account => `${account.name} (${account.reason})`)
                  .join(', '),
              },
            )}
          </Text>
        )}
        {failure ? (
          <Text style={{ fontSize: 13, color: theme.errorText }}>
            {failure}
          </Text>
        ) : (
          updateReady &&
          !busy && (
            <Text style={{ fontSize: 13, color: theme.warningText }}>
              <Trans>
                L'application se met à jour toute seule à l'ouverture ; ce
                bouton le fait tout de suite.
              </Trans>
            </Text>
          )
        )}
        <Button
          isDisabled={busy !== null}
          onPress={() => void update()}
          style={{ minHeight: 48 }}
        >
          {busy === 'update'
            ? t('Mise à jour en cours…')
            : t("Mettre à jour l'application")}
        </Button>
        <Button
          isDisabled={busy !== null}
          onPress={() => void refresh()}
          style={{ minHeight: 48 }}
        >
          {busy === 'refresh'
            ? t('Actualisation en cours…')
            : t('Actualiser les données')}
        </Button>
      </View>
    </Section>
  );
}
